import { InventoryService, isInventoryDecryptError } from './inventoryService';
import {
  STEP1_FIRST_STEP_INVENTORY_TYPE,
  STEP1_FIRST_STEP_VERSION,
  Step1WritingInventoryPayload,
  Step1WritingMetadata,
} from '../types/step1Writing';
import { mergeCoverageStatus } from '../utils/step1CoverageChecker';
import { estimateReadTime } from '../utils/readTimeEstimate';
import {
  archiveStep1Payload,
  deleteAllStep1Archives,
  markAutosaveArchiveCreated,
  payloadHasMeaningfulStep1Content,
  shouldThrottleAutosaveArchive,
  step1PayloadDigest,
  loadStep1PayloadFromVersion,
  listStep1ArchiveSummaries,
  wizardAnswersHaveContent,
} from '../utils/step1WritingArchive';
import { clearStep1LocalBackup, writeStep1LocalBackup } from '../utils/step1WritingLocalBackup';
import { STEP1_LIFE_STORY_PROMPTS } from '../config/step1LifeStoryPrompts';

export { listStep1ArchiveSummaries, type Step1ArchiveSummary } from '../utils/step1WritingArchive';
export { wizardAnswersHaveContent, payloadHasMeaningfulStep1Content } from '../utils/step1WritingArchive';
export { readStep1LocalBackup, type Step1LocalBackupRecord } from '../utils/step1WritingLocalBackup';

const PAYLOAD_TYPE = 'step1_writing_payload';

export type Step1SaveKind = 'autosave' | 'destructive' | 'restore' | 'manual' | 'flush';

export interface Step1SaveOptions {
  kind?: Step1SaveKind;
  archiveReason?: string;
  skipArchive?: boolean;
  localBackupUserKey?: string;
  /** Replace a copy we cannot decrypt. Does not affect other users or other inventories. */
  overwriteUnreadable?: boolean;
}

function emptyPayload(): Step1WritingInventoryPayload {
  return {
    _type: PAYLOAD_TYPE,
    wizardAnswers: {},
    wizardStepIndex: 0,
    wizardChapterId: 'ch1',
    lastSavedAt: new Date().toISOString(),
  };
}

export async function loadStep1WritingPayload(): Promise<Step1WritingInventoryPayload> {
  try {
    const items = await InventoryService.getInventory(
      STEP1_FIRST_STEP_INVENTORY_TYPE,
      STEP1_FIRST_STEP_VERSION
    );
    const payload = items.find((i) => i._type === PAYLOAD_TYPE) as Step1WritingInventoryPayload | undefined;
    return payload ? { ...emptyPayload(), ...payload, _type: PAYLOAD_TYPE } : emptyPayload();
  } catch (err) {
    if (isInventoryDecryptError(err)) {
      throw new Error('STEP1_WRITING_DECRYPT_FAILED');
    }
    throw err;
  }
}

/** True when incoming save would replace substantial saved work with an empty payload. */
export function wouldWipeStep1WritingPayload(
  existing: Step1WritingInventoryPayload,
  incoming: Step1WritingInventoryPayload
): boolean {
  if (
    wizardAnswersHaveContent(existing.wizardAnswers) &&
    !wizardAnswersHaveContent(incoming.wizardAnswers)
  ) {
    return true;
  }
  const existingLen = (existing.contentText || '').trim().length;
  const incomingLen = (incoming.contentText || '').trim().length;
  if (existingLen >= 50 && incomingLen === 0) {
    return true;
  }
  return false;
}

export function mergeStep1WritingPayloads(
  existing: Step1WritingInventoryPayload,
  incoming: Step1WritingInventoryPayload
): Step1WritingInventoryPayload {
  const wizardAnswers =
    wizardAnswersHaveContent(incoming.wizardAnswers) ||
    !wizardAnswersHaveContent(existing.wizardAnswers)
      ? { ...(existing.wizardAnswers || {}), ...(incoming.wizardAnswers || {}) }
      : existing.wizardAnswers || {};
  return {
    ...existing,
    ...incoming,
    wizardAnswers,
    _type: PAYLOAD_TYPE,
  };
}

async function maybeArchiveBeforeSave(
  existing: Step1WritingInventoryPayload,
  merged: Step1WritingInventoryPayload,
  options: Step1SaveOptions
): Promise<void> {
  if (options.skipArchive || !payloadHasMeaningfulStep1Content(existing)) return;

  const digestChanged = step1PayloadDigest(existing) !== step1PayloadDigest(merged);
  if (!digestChanged) return;

  const kind = options.kind || 'autosave';
  const shouldArchive =
    kind === 'destructive' ||
    kind === 'restore' ||
    kind === 'manual' ||
    kind === 'flush' ||
    (kind === 'autosave' && !shouldThrottleAutosaveArchive());

  if (!shouldArchive) return;

  const reason =
    options.archiveReason ||
    (kind === 'autosave' ? 'Automatic snapshot while editing' : `Before ${kind} save`);
  await archiveStep1Payload(existing, reason);
  if (kind === 'autosave') markAutosaveArchiveCreated();
}

/** Snapshot current server copy before a confirmed bulk overwrite (assemble, upload, template, etc.). */
export async function archiveBeforeDestructiveStep1Edit(reason: string): Promise<void> {
  const existing = await loadStep1WritingPayload();
  if (payloadHasMeaningfulStep1Content(existing)) {
    await archiveStep1Payload(existing, reason);
  }
}

export async function saveStep1WritingPayload(
  payload: Step1WritingInventoryPayload,
  options: Step1SaveOptions = {}
): Promise<void> {
  let existing: Step1WritingInventoryPayload;
  try {
    existing = await loadStep1WritingPayload();
  } catch (err) {
    if (options.overwriteUnreadable && err instanceof Error && err.message === 'STEP1_WRITING_DECRYPT_FAILED') {
      existing = emptyPayload();
    } else {
      throw new Error('STEP1_WRITING_LOAD_FAILED');
    }
  }
  if (wouldWipeStep1WritingPayload(existing, payload)) {
    throw new Error('STEP1_WRITING_WIPE_BLOCKED');
  }
  const merged = mergeStep1WritingPayloads(existing, payload);
  await maybeArchiveBeforeSave(existing, merged, options);
  const normalized: Step1WritingInventoryPayload = {
    ...merged,
    _type: PAYLOAD_TYPE,
    lastSavedAt: new Date().toISOString(),
  };
  await InventoryService.saveInventory(STEP1_FIRST_STEP_INVENTORY_TYPE, STEP1_FIRST_STEP_VERSION, [
    normalized,
  ]);
  writeStep1LocalBackup(normalized, options.localBackupUserKey);
}

/** Replace the live draft entirely (used when restoring an archive or local backup). */
export async function replaceStep1WritingPayload(
  payload: Step1WritingInventoryPayload,
  options: Step1SaveOptions & { archiveCurrent?: boolean } = {}
): Promise<void> {
  let existing: Step1WritingInventoryPayload;
  try {
    existing = await loadStep1WritingPayload();
  } catch (err) {
    if (options.overwriteUnreadable && err instanceof Error && err.message === 'STEP1_WRITING_DECRYPT_FAILED') {
      existing = emptyPayload();
    } else {
      throw new Error('STEP1_WRITING_LOAD_FAILED');
    }
  }
  if (options.archiveCurrent !== false && payloadHasMeaningfulStep1Content(existing)) {
    await archiveStep1Payload(
      existing,
      options.archiveReason || 'Before restoring an earlier copy'
    );
  }
  const normalized: Step1WritingInventoryPayload = {
    ...payload,
    _type: PAYLOAD_TYPE,
    lastSavedAt: new Date().toISOString(),
  };
  await InventoryService.saveInventory(STEP1_FIRST_STEP_INVENTORY_TYPE, STEP1_FIRST_STEP_VERSION, [
    normalized,
  ]);
  writeStep1LocalBackup(normalized, options.localBackupUserKey);
}

/** After a decrypt mismatch: write a new empty draft with the current key. */
export async function startFreshStep1WritingPayload(
  localBackupUserKey?: string
): Promise<Step1WritingInventoryPayload> {
  const fresh = emptyPayload();
  await replaceStep1WritingPayload(fresh, {
    kind: 'manual',
    archiveCurrent: false,
    overwriteUnreadable: true,
    skipArchive: true,
    localBackupUserKey,
  });
  return fresh;
}

export async function restoreStep1FromArchive(
  version: string,
  localBackupUserKey?: string
): Promise<Step1WritingInventoryPayload> {
  const archived = await loadStep1PayloadFromVersion(version);
  if (!archived) {
    throw new Error('STEP1_ARCHIVE_NOT_FOUND');
  }
  await replaceStep1WritingPayload(archived, {
    kind: 'restore',
    archiveReason: `Before restoring snapshot from ${version}`,
    localBackupUserKey,
  });
  return archived;
}

export function buildMetadataFromState(
  metadata: Step1WritingMetadata,
  payload: Step1WritingInventoryPayload
): Step1WritingMetadata {
  const contentText = payload.contentText || '';
  const estimate = estimateReadTime(contentText);
  const coverageStatus = mergeCoverageStatus(
    payload.wizardAnswers || {},
    payload.coverageManualMarks,
    contentText
  );

  const hasWizardContent = Object.values(payload.wizardAnswers || {}).some(
    (e) => e.examples?.some((x) => x.trim().length > 0)
  );
  let wizardPromptsFilled = 0;
  let wizardPromptsSkipped = 0;
  for (const e of Object.values(payload.wizardAnswers || {})) {
    if (e.examples?.some((x) => x.trim().length > 0)) {
      wizardPromptsFilled += 1;
    } else if (e.status === 'skipped') {
      wizardPromptsSkipped += 1;
    }
  }
  const hasUpload = !!(payload.originalFileName && contentText.trim());
  const hasExternalNote = !!(metadata.externalNote?.trim());
  const hasTyped = metadata.mode === 'typed' && contentText.trim().length > 0;

  let hasContent = false;
  if (!metadata.pathSelected || !metadata.mode) {
    hasContent =
      hasWizardContent ||
      hasUpload ||
      hasTyped ||
      hasExternalNote ||
      contentText.trim().length > 0;
  } else {
    switch (metadata.mode) {
      case 'external':
        hasContent = hasExternalNote || metadata.externalAcknowledged === true;
        break;
      case 'upload':
        hasContent = hasUpload;
        break;
      case 'typed':
        hasContent = hasTyped;
        break;
      case 'wizard':
        hasContent = hasWizardContent || contentText.trim().length > 0;
        break;
    }
  }

  const now = new Date().toISOString();
  return {
    ...metadata,
    wordCount: estimate.wordCount,
    speakingMinutesEstimate: {
      min: estimate.speakMinMinutes,
      max: estimate.speakMaxMinutes,
    },
    coverageStatus,
    presentationPlan: payload.presentationPlan || metadata.presentationPlan,
    wizardChapterId: payload.wizardChapterId,
    wizardStepIndex: payload.wizardStepIndex,
    wizardPromptsTotal: STEP1_LIFE_STORY_PROMPTS.length,
    wizardPromptsFilled,
    wizardPromptsSkipped,
    hasContent,
    lastUpdated: now,
  };
}

export function downloadOriginalFile(payload: Step1WritingInventoryPayload): void {
  if (!payload.originalFileBase64 || !payload.originalFileName) return;
  const mime = payload.originalMimeType || 'application/octet-stream';
  const binary = atob(payload.originalFileBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = payload.originalFileName;
  a.click();
  URL.revokeObjectURL(url);
}

/** Remove all First Step story data from Recovery Commons (live draft and all stored copies). */
export async function clearStep1WritingAll(localBackupUserKey?: string): Promise<Step1WritingInventoryPayload> {
  await deleteAllStep1Archives();
  const normalized: Step1WritingInventoryPayload = {
    ...emptyPayload(),
    _type: PAYLOAD_TYPE,
    lastSavedAt: new Date().toISOString(),
  };
  await InventoryService.saveInventory(STEP1_FIRST_STEP_INVENTORY_TYPE, STEP1_FIRST_STEP_VERSION, [
    normalized,
  ]);
  clearStep1LocalBackup(localBackupUserKey);
  return normalized;
}
